import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useState } from "react";
import {
	Autocomplete,
	Chip,
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	Grid2,
	MenuItem,
	TextField,
} from "@mui/material";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import type {
	MillSetupGridRow,
	PassIdOption,
	ProductMillSetup,
} from "../models/ProductMillSetup";
import { PRODUCT_MILL_SETUP_INITIAL_VALUES } from "../models/ProductMillSetup";
import {
	useActivePassIds,
	useActiveStands,
} from "../hooks/useProductMillSetup";

interface ProductMillSetupFormModalProps {
	open: boolean;
	productSizeId: number;
	/** An empty grid line opens the form to add a setup on that stand. */
	initialData?: MillSetupGridRow | null;
	/** Stands that already have a setup: a new one goes on a free stand. */
	configuredStandIds: Set<number>;
	onSave: (setup: ProductMillSetup) => void;
	onCancel: () => void;
	isSaving: boolean;
}

export const ProductMillSetupFormModal = ({
	open,
	productSizeId,
	initialData,
	configuredStandIds,
	onSave,
	onCancel,
	isSaving,
}: ProductMillSetupFormModalProps) => {
	const isEditing = !!initialData && !initialData.IsEmpty;

	const [formData, setFormData] = useState<ProductMillSetup>({
		...PRODUCT_MILL_SETUP_INITIAL_VALUES,
		ProductSizeId: productSizeId,
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	const { data: stands = [] } = useActiveStands();
	const { data: passIds = [] } = useActivePassIds();

	useEffect(() => {
		if (open) {
			if (initialData && !initialData.IsEmpty) {
				setFormData(sanitizeNullStrings({ ...initialData }));
			} else if (initialData?.IsEmpty) {
				setFormData({
					...PRODUCT_MILL_SETUP_INITIAL_VALUES,
					ProductSizeId: productSizeId,
					StandId: initialData.StandId,
				});
			} else {
				setFormData({
					...PRODUCT_MILL_SETUP_INITIAL_VALUES,
					ProductSizeId: productSizeId,
				});
			}
			setErrors({});
		}
	}, [open, initialData, productSizeId]);

	const handleChange = (field: keyof ProductMillSetup, value: unknown) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		setErrors((prev) => ({ ...prev, [field]: "" }));
	};

	// Passes usable on the stand: its own, plus the ones configured for several stands.
	const isPassForStand = (pass: PassIdOption, standId: number | null) =>
		standId != null &&
		(pass.StandId === standId ||
			(pass.ExtraStandIds ?? "").split(",").includes(String(standId)));

	const alternativeIds = (formData.AlternativePassIds ?? "")
		.split(",")
		.map(Number)
		.filter(Boolean);
	const mainPass = passIds.find((p) => p.Id === formData.PassId) ?? null;
	const alternativePasses = alternativeIds
		.map((id) => passIds.find((p) => p.Id === id))
		.filter((p): p is PassIdOption => p != null);

	// The stand's own passes first, then the ones it borrows from other stands.
	const passOptions = passIds
		.filter(
			(p) =>
				isPassForStand(p, formData.StandId) ||
				p.Id === formData.PassId ||
				alternativeIds.includes(p.Id),
		)
		.sort(
			(a, b) =>
				Number(a.StandId !== formData.StandId) -
				Number(b.StandId !== formData.StandId),
		);
	const alternativeOptions = passOptions.filter(
		(p) => p.Id !== formData.PassId,
	);

	const setAlternatives = (passes: PassIdOption[]) =>
		setFormData((prev) => ({
			...prev,
			AlternativePassIds: passes.map((p) => p.Id).join(","),
		}));

	const setMainPass = (pass: PassIdOption | null) => {
		setFormData((prev) => ({
			...prev,
			PassId: pass?.Id ?? null,
			PassIdName: pass?.Name ?? null,
			AlternativePassIds: (prev.AlternativePassIds ?? "")
				.split(",")
				.filter((id) => id && Number(id) !== pass?.Id)
				.join(","),
		}));
		setErrors((prev) => ({ ...prev, PassId: "" }));
	};

	const handleStandChange = (standId: number | null) => {
		setFormData((prev) => ({ ...prev, StandId: standId }));
		setMainPass(
			mainPass && isPassForStand(mainPass, standId) ? mainPass : null,
		);
		setAlternatives(
			alternativePasses.filter((p) => isPassForStand(p, standId)),
		);
		setErrors((prev) => ({ ...prev, StandId: "" }));
	};

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};
		if (!formData.StandId) {
			newErrors.StandId = "Stand is required";
		}
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = () => {
		if (!validate()) return;
		onSave({ ...formData, ProductSizeId: productSizeId });
	};

	return (
		<Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
			<DetailHeader
				title={isEditing ? "Edit Mill Setup" : "Add Mill Setup"}
				isSaving={isSaving}
				onClose={onCancel}
			/>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				<DialogContent sx={{ p: 2 }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<DetailSection title="Passes" icon={<FormatListNumberedIcon />}>
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<TextField
										select
										label="Stand"
										value={formData.StandId ?? ""}
										onChange={(e) =>
											handleStandChange(
												e.target.value === "" ? null : Number(e.target.value),
											)
										}
										fullWidth
										size="small"
										required
										error={!!errors.StandId}
										helperText={errors.StandId}
										disabled={isEditing}
										slotProps={{
											inputLabel: { shrink: true },
											select: { sx: { width: "100%" } },
										}}
									>
										<MenuItem value="">
											<em>None</em>
										</MenuItem>
										{stands.map((s) => (
											<MenuItem
												key={s.Id}
												value={s.Id}
												disabled={!isEditing && configuredStandIds.has(s.Id)}
											>
												{s.Name}
											</MenuItem>
										))}
									</TextField>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<Autocomplete
										options={passOptions}
										value={mainPass}
										autoHighlight
										size="small"
										fullWidth
										disabled={formData.StandId == null}
										getOptionLabel={(option) => option.Name}
										isOptionEqualToValue={(option, current) =>
											option.Id === current.Id
										}
										onChange={(_event, pass) => setMainPass(pass)}
										renderOption={(props, option) => {
											const { key, ...rest } = props as typeof props & {
												key: string;
											};
											return (
												<li key={option.Id} {...rest}>
													{option.Name}
												</li>
											);
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Main Pass"
												error={!!errors.PassId}
												helperText={errors.PassId}
												slotProps={{ inputLabel: { shrink: true } }}
											/>
										)}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12 }}>
									<Autocomplete
										multiple
										options={alternativeOptions}
										value={alternativePasses}
										autoHighlight
										disableCloseOnSelect
										size="small"
										fullWidth
										disabled={formData.PassId == null}
										getOptionLabel={(option) => option.Name}
										isOptionEqualToValue={(option, current) =>
											option.Id === current.Id
										}
										onChange={(_event, passes) => setAlternatives(passes)}
										renderTags={(passes, getTagProps) =>
											passes.map((pass, index) => {
												const { key, ...tagProps } = getTagProps({ index });
												return (
													<Chip
														key={key}
														{...tagProps}
														size="small"
														label={pass.Name}
													/>
												);
											})
										}
										renderOption={(props, option) => {
											const { key, ...rest } = props as typeof props & {
												key: string;
											};
											return (
												<li key={option.Id} {...rest}>
													{option.Name}
												</li>
											);
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Alternative Passes"
												slotProps={{ inputLabel: { shrink: true } }}
											/>
										)}
									/>
								</Grid2>
							</Grid2>
						</DetailSection>
						<DetailSection title="Dimensions" icon={<StraightenOutlinedIcon />}>
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<TextField
										label="Bar Size"
										value={formData.BarSize ?? ""}
										onChange={(e) => handleChange("BarSize", e.target.value)}
										fullWidth
										size="small"
										slotProps={{ inputLabel: { shrink: true } }}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<TextField
										label="Rolled Al"
										value={formData.RolledAl ?? ""}
										onChange={(e) =>
											handleChange("RolledAl", e.target.value || null)
										}
										fullWidth
										size="small"
										slotProps={{ inputLabel: { shrink: true } }}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<TextField
										label="Pass Depth"
										type="number"
										value={formData.PassDepth ?? ""}
										onChange={(e) =>
											handleChange(
												"PassDepth",
												e.target.value === ""
													? null
													: Number.parseFloat(e.target.value),
											)
										}
										fullWidth
										size="small"
										slotProps={{
											inputLabel: { shrink: true },
											htmlInput: { step: 0.001 },
										}}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<TextField
										label="Pass Width"
										type="number"
										value={formData.PassWidth ?? ""}
										onChange={(e) =>
											handleChange(
												"PassWidth",
												e.target.value === ""
													? null
													: Number.parseFloat(e.target.value),
											)
										}
										fullWidth
										size="small"
										slotProps={{
											inputLabel: { shrink: true },
											htmlInput: { step: 0.001 },
										}}
									/>
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<TextField
										label="Roll Part"
										type="number"
										value={formData.RollPart ?? ""}
										onChange={(e) =>
											handleChange(
												"RollPart",
												e.target.value === ""
													? null
													: Number.parseFloat(e.target.value),
											)
										}
										fullWidth
										size="small"
										slotProps={{
											inputLabel: { shrink: true },
											htmlInput: { step: 0.001 },
										}}
									/>
								</Grid2>
							</Grid2>
						</DetailSection>
						<DetailSection title="Comments" icon={<ChatBubbleOutlineIcon />}>
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12 }}>
									<TextField
										label="Comments"
										value={formData.Comments ?? ""}
										onChange={(e) => handleChange("Comments", e.target.value)}
										fullWidth
										multiline
										minRows={2}
										maxRows={6}
										size="small"
										slotProps={{ inputLabel: { shrink: true } }}
									/>
								</Grid2>
							</Grid2>
						</DetailSection>
					</Box>
				</DialogContent>
				<Divider />
				<DialogActions sx={{ px: 2, py: 1.5 }}>
					<FooterModalButtons
						isSubmitLoading={isSaving}
						onHide={onCancel}
						buttonLabel="Save"
					/>
				</DialogActions>
			</form>
		</Dialog>
	);
};
